import { useState, useCallback, useEffect } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { useToast } from "@chakra-ui/react";
import { cn } from "utils/helpers";
import { useToggleScoring } from "../../../../hooks/usePanelScoringQueries";
import { useReviewDisclaimer } from "../../../../hooks/useReviewDisclaimer";
import { DisclaimerModal } from "newui/features/Rfx/Evaluation/components/DisclaimerModal";

type LeadScoreVisibility = "always" | "only_when_not_scoring" | "only_after_all_submitted";
type DisclaimerStatus = "no_conflict" | "conflict" | "reviewed" | null;

interface LeadScoringToggleProps {
	anyMemberSubmitted?: boolean;
	/** When true, a configured disclaimer must be completed before the lead scores. */
	disclaimerEnabled?: boolean;
	evalId: number;
	initialIsScoring: boolean;
	/** Current disclaimer status of the lead's panel-member record (if any). */
	leadDisclaimerStatus?: DisclaimerStatus;
	/** The lead's panel-member id, present once they are a scoring member. */
	leadMemberId?: number;
	leadScoreVisibility?: LeadScoreVisibility;
	rfxId: number;
	onToggle?: (isScoring: boolean) => void;
}

export const LeadScoringToggle = ({
	rfxId,
	evalId,
	initialIsScoring,
	leadScoreVisibility = "always",
	anyMemberSubmitted = false,
	disclaimerEnabled = false,
	leadMemberId,
	leadDisclaimerStatus = null,
	onToggle,
}: LeadScoringToggleProps) => {
	const [isScoring, setIsScoring] = useState(initialIsScoring);
	const [showDisclaimer, setShowDisclaimer] = useState(false);
	const [disclaimerMemberId, setDisclaimerMemberId] = useState<number | null>(
		leadMemberId ?? null
	);
	// Once the lead completes (or opts out of) their disclaimer this session, stop
	// re-prompting while the query cache catches up with the new status.
	const [disclaimerHandled, setDisclaimerHandled] = useState(false);
	// PRO-664: turning scoring OFF clears the lead's disclaimer server-side, so the next
	// enable must prompt again. Without this the sticky `disclaimerHandled` flag (and a
	// stale cached `leadDisclaimerStatus`) suppressed the modal, leaving the lead's row
	// stuck on "Pending" with no way to clear it — which blocked the next step.
	const [disclaimerReset, setDisclaimerReset] = useState(false);

	const toggleMutation = useToggleScoring(rfxId, evalId);
	const reviewMutation = useReviewDisclaimer(rfxId, evalId);
	const toast = useToast();

	// Keep the switch in sync with the authoritative scoring state. The parent
	// derives `initialIsScoring` from the (re-fetched) panel-member data after a
	// toggle, so without this the switch would freeze on its initial captured
	// value — showing the wrong position while the page content updated.
	useEffect(() => {
		setIsScoring(initialIsScoring);
	}, [initialIsScoring]);

	// Track the lead's member id once they become a panel member.
	useEffect(() => {
		if (leadMemberId) {
			setDisclaimerMemberId(leadMemberId);
		}
	}, [leadMemberId]);

	const disclaimerSatisfied =
		leadDisclaimerStatus === "no_conflict" || leadDisclaimerStatus === "reviewed";
	const needsDisclaimer =
		disclaimerEnabled && (disclaimerReset || (!disclaimerSatisfied && !disclaimerHandled));

	const disableScoring = useCallback(() => {
		setIsScoring(false);
		setDisclaimerReset(true);
		onToggle?.(false);
		toggleMutation.mutate(
			{ is_scoring: false },
			{
				onError: () => {
					setIsScoring(true);
					onToggle?.(true);
				},
			}
		);
	}, [toggleMutation, onToggle]);

	const enableScoring = useCallback(() => {
		setIsScoring(true);
		onToggle?.(true);
		toggleMutation.mutate(
			{ is_scoring: true },
			{
				onSuccess: (member) => {
					// Enabling scoring created/returned the lead's panel-member record,
					// so we now have the id to attach a disclaimer to. If a disclaimer
					// is configured and not yet satisfied, prompt for it.
					if (needsDisclaimer) {
						setDisclaimerMemberId(member?.id ?? leadMemberId ?? null);
						setShowDisclaimer(true);
					}
				},
				onError: () => {
					setIsScoring(false);
					onToggle?.(false);
				},
			}
		);
	}, [toggleMutation, onToggle, needsDisclaimer, leadMemberId]);

	const handleToggle = useCallback(() => {
		const newValue = !isScoring;

		// Prevent enabling scoring after members have submitted when visibility is restricted
		if (newValue && leadScoreVisibility !== "always" && anyMemberSubmitted) {
			toast({
				title: "Cannot enable scoring",
				description: "Cannot enable scoring after panel members have submitted scores.",
				status: "warning",
				duration: 5000,
				isClosable: true,
			});
			return;
		}

		if (newValue) {
			enableScoring();
		} else {
			disableScoring();
		}
	}, [isScoring, leadScoreVisibility, anyMemberSubmitted, toast, enableScoring, disableScoring]);

	const handleDisclaimerSuccess = useCallback(
		(hasConflict: boolean) => {
			setShowDisclaimer(false);
			setDisclaimerHandled(true);
			setDisclaimerReset(false);
			// The RFx Lead is the reviewer, so their own declared conflict is
			// authorised automatically the moment it is saved.
			if (hasConflict && disclaimerMemberId) {
				reviewMutation.mutate({
					memberId: disclaimerMemberId,
					payload: {
						action: "accepted",
						notes: "Automatically authorised — conflict declared by the RFx Lead.",
					},
				});
			}
			// Refresh authoritative scoring/disclaimer state in the parent.
			onToggle?.(true);
		},
		[disclaimerMemberId, reviewMutation, onToggle]
	);

	const handleDisclaimerCancel = useCallback(() => {
		setShowDisclaimer(false);
		// Cancelling the disclaimer means the lead does not score — revert the switch.
		disableScoring();
	}, [disableScoring]);

	return (
		<>
			<div
				className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-100 px-4 py-3"
				data-testid="lead-scoring-toggle"
			>
				<div className="flex items-center gap-3">
					<div className="flex items-center gap-3">
						{toggleMutation.isError && (
							<span className="inline-flex items-center gap-1 text-xs text-red-600">
								<FiAlertCircle size={12} />
								Failed to update
							</span>
						)}
						<button
							type="button"
							role="switch"
							aria-checked={isScoring}
							aria-label="Toggle lead scoring participation"
							onClick={handleToggle}
							disabled={toggleMutation.isPending}
							className={cn(
								"relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2",
								isScoring ? "bg-[#0031DD]" : "bg-gray-300",
								toggleMutation.isPending && "opacity-50 cursor-not-allowed"
							)}
							data-testid="scoring-toggle-switch"
						>
							<span
								className={cn(
									"pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm ring-0 transition-transform",
									isScoring ? "translate-x-6" : "translate-x-1"
								)}
							/>
						</button>
					</div>
					<div>
						<p className="text-sm font-medium text-gray-900">
							I am scoring this evaluation
						</p>
						<p className="text-xs text-gray-500">
							{isScoring
								? "You will score suppliers alongside other panel members."
								: "You will not score suppliers — only manage the panel."}
						</p>
					</div>
				</div>
			</div>

			{showDisclaimer && disclaimerMemberId != null && (
				<DisclaimerModal
					isOpen={showDisclaimer}
					onClose={handleDisclaimerCancel}
					onSubmitSuccess={handleDisclaimerSuccess}
					skipCancelMutation
					rfxId={rfxId}
					evalId={evalId}
					memberId={disclaimerMemberId}
				/>
			)}
		</>
	);
};
